import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { Observable, Subject, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { AddClockHourComponent } from './add-clock-hour.component';
import { AddClockHour } from './add-clock-hour.types';
import { StorageService } from '@web-frontend/shared/services';
import { PermissionService } from '@web-frontend/shared/services/permissions';
import { FeatureUser } from '@tacliatech/types';

@Injectable({
  providedIn: 'root',
})
export class AddClockHourService {
  private refreshListSource = new Subject<boolean>();

  refreshList$ = this.refreshListSource.asObservable();

  constructor(
    private dialog: MatDialog,
    private permissionService: PermissionService
  ) {}

  public refreshList() {
    this.refreshListSource.next(true);
  }

  private openModal(config: MatDialogConfig<AddClockHour.Params>) {
    const dialogRef = this.dialog.open(AddClockHourComponent, config);
    return dialogRef.afterClosed().pipe(
      tap((res) => {
        this.refreshListSource.next(res);
      })
    );
  }

  private canUserSeeTeamClockHour(): Observable<boolean> {
    return this.permissionService
      .getFeaturePermissionValues([FeatureUser.ClockHourCompany.show])
      .pipe(map((permission) => permission[FeatureUser.ClockHourCompany.show]));
  }

  private isAdminUser(): boolean {
    const userData = StorageService.userData as { role?: string };
    return userData?.role === 'admin';
  }

  private isCurrentUser(idOwner: string): boolean {
    return StorageService.UserId === idOwner;
  }

  open(config: MatDialogConfig<AddClockHour.Params> = new MatDialogConfig()) {
    const { clockInWithStopwatchMandatory, idOwner } = config.data || {};

    // Admin users always have access
    if (this.isAdminUser()) {
      return this.openModal(config);
    }

    // If user is viewing their own clock hours and stopwatch is not mandatory
    if (this.isCurrentUser(idOwner) && !clockInWithStopwatchMandatory) {
      return this.openModal(config);
    }

    // For team members' clock hours, check permissions
    if (!this.isCurrentUser(idOwner)) {
      return this.canUserSeeTeamClockHour().pipe(
        switchMap((hasPermission) => {
          if (hasPermission) {
            return this.openModal(config);
          } else {
            return of(null);
          }
        })
      );
    }

    // Default fallback
    return of(null);
  }
}
